import {
  ADMIN_CONCIERGE,
  ADMIN_DASHBOARD,
  Button,
  CaptchaSlot,
  Disclaimer,
  Div,
  getDevLoginOtp,
  H3,
  H5,
  P,
  Separator,
  handlePartnerAuthNavigation,
  isAdmin,
  isBrandOwner,
  isPartner,
  isSales,
  lightTheme,
  resendOtpEmail,
  resendOtpEmailV2,
  signIn,
  signInCaptchaAction,
  signInV2,
  useCaptcha,
  verifyPartnerSms,
  reportError,
} from '@vgrubs/components';
import { UserRole } from 'components/src/types/user.types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import OtpInput from 'react18-input-otp';
import styled from 'styled-components';
import { AdminSpinner, ErrorToast, SuccessToast } from '../../adminComponents';
import { useAuth } from '../../store/auth';

const Form = styled.form`
  width: 100%;
`;

/**
 * The dev backend answers the live code for a number (`get-login-otp`), so
 * QA needs no phone. The route does not exist in production, so the button
 * goes by where the page is served: any localhost, and the dev hosts
 * (`admin-dev.vgrubs.com`, `merchant-dev.vgrubs.com`, ...). Never in prod.
 */
const DEV_HOST = /^(localhost|127\.0\.0\.1|[a-z0-9-]+-dev\.vgrubs\.com)$/;
// Guarded: the barrel reaches this module from the Next apps' server render
// (vgrubs app/error.tsx), where there is no window. A bare read here failed
// every prerender of the website build.
const IS_DEV_HOST =
  typeof window !== 'undefined' && DEV_HOST.test(window.location.hostname);

export const OtpAdmin = () => {
  const { updateAuthState, updatePartnerAuthState, updateLastLogin } = useAuth(
    (state) => state
  );
  const [code, setCode] = useState('');
  const onChange = (value: string) => {
    setCode(value);
  };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const canFetchCode = IS_DEV_HOST && !isSales;
  const { loginPhoneNumber, loginHasEmail, loginIsNewUser } = useAuth();
  const ref = useRef(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const { getCaptchaToken } = useCaptcha(
    process.env.NX_TURNSTILE_SITE_KEY_SIGN_IN,
    signInCaptchaAction(),
    captchaRef,
    // Hide the loader while the user is solving an interactive challenge.
    (isInteractive) => isInteractive && setLoading(false)
  );

  const role = (): UserRole => {
    if (isAdmin) return UserRole.admin;
    if (isBrandOwner) return UserRole.brandOwner;
    if (isSales) return UserRole.manager;
    return UserRole.partner;
  };

  async function resend(request: () => Promise<{ data: { message: string } }>) {
    try {
      setLoading(true);

      const { data } = await request();

      setLoading(false);
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function resendCode(isViaEmail: boolean) {
    const phoneNumber = loginPhoneNumber || '';

    // Merchant is on v2 (no role), like the login page that sent the first code.
    if (isViaEmail) {
      return resend(() =>
        isPartner
          ? resendOtpEmailV2({ phoneNumber })
          : resendOtpEmail({ role: role(), phoneNumber })
      );
    }

    // Resending by SMS re-hits /auth/sign-in, which is captcha-protected —
    // same endpoint the login page guards.
    setLoading(true);
    const token = await getCaptchaToken();
    if (!token) {
      setLoading(false);
      return;
    }

    return resend(() =>
      isPartner
        ? signInV2({ phoneNumber, captcha: { token } })
        : signIn({ role: role(), phoneNumber, captcha: { token } })
    );
  }

  async function handleSignIn() {
    try {
      setLoading(true);

      const { data } = await verifyPartnerSms({
        phoneNumber: loginPhoneNumber || '',
        code,
      });

      setLoading(false);

      if (isPartner) {
        updatePartnerAuthState(data.data);
      } else {
        updateAuthState(data.data);
      }

      updateLastLogin(new Date().toISOString());
      isAdmin && navigate(ADMIN_DASHBOARD);
      isPartner && handlePartnerAuthNavigation(data.data);
      isSales && navigate(ADMIN_CONCIERGE);
      isBrandOwner && navigate(ADMIN_DASHBOARD);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleFetchCode() {
    if (!loginPhoneNumber) return;
    try {
      setFetching(true);
      const { data } = await getDevLoginOtp(loginPhoneNumber);
      if (!data.data) return ErrorToast('No code came back for this number');
      // Setting the code submits it through the effect below.
      setCode(String(data.data));
    } catch (error) {
      reportError(error);
    } finally {
      setFetching(false);
    }
  }

  function handleSubmit() {
    if (code.length < 4) {
      ErrorToast('Enter 4 digit code');
    } else {
      handleSignIn();
    }
  }

  useEffect(() => {
    if (code.length === 4) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  /*
   * Fill the code from the SMS on Android.
   *
   * iOS does this from the `autoComplete="one-time-code"` attribute on the
   * inputs below, offering the code above the keyboard. Chrome ignores that
   * attribute and reads the message itself through this API instead - no
   * keyboard step at all - but only for an SMS whose LAST line names this
   * exact origin:
   *
   *     @admin-dev.vgrubs.com #1234
   *
   * So it stays silent until the backend appends that line, per origin, and
   * costs nothing in the meantime. Filling the code submits it, through the
   * effect above.
   */
  useEffect(() => {
    if (!('OTPCredential' in window)) return;

    // Not in the DOM lib yet, so the shape is spelled out here.
    type OtpRequest = CredentialRequestOptions & {
      otp: { transport: string[] };
    };

    const abort = new AbortController();

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: abort.signal } as OtpRequest)
      .then((credential) => {
        const received = (credential as unknown as { code?: string } | null)
          ?.code;
        if (received) setCode(received);
      })
      .catch(() => {
        /* Dismissed, timed out, or an SMS that did not name this origin. */
      });

    return () => abort.abort();
  }, []);

  return (
    <>
      {loading && <AdminSpinner />}

      <H3 $m="0">
        <span>Confirm Phone Number</span>
      </H3>
      <Separator $m="30px 0" />

      <P>
        We sent a 4-digit code to {loginPhoneNumber} <br /> To validate your
        account insert received code below.
      </P>
      {isPartner && loginIsNewUser && (
        <Disclaimer $m="0 0 16px">
          We're creating a new restaurant owner account for this number.
        </Disclaimer>
      )}
      <H5>Enter Code</H5>

      <Form>
        <Div $flex $justifyCenter>
          <OtpInput
            isInputNum
            shouldAutoFocus
            // What makes iOS offer the code from Messages above the keyboard.
            // The component puts it on every input, which is what Safari
            // expects for a split field.
            autoComplete="one-time-code"
            ref={ref}
            value={code}
            onChange={onChange}
            numInputs={4}
            inputStyle={{
              height: 45,
              width: 45,
              borderRadius: '50%',
              margin: '0 8px',
              border: `1px solid ${lightTheme.darkGray}`,
              fontSize: '16px',
              background: 'none',
            }}
            focusStyle={{
              outline: 'none',
              border: `1px solid ${lightTheme.primary}`,
            }}
          />
        </Div>
        <Disclaimer $m="24px 0 16px 0">Did not receive the code?</Disclaimer>
        <CaptchaSlot ref={captchaRef} />
        <Button
          type="button"
          onClick={() => resendCode(false)}
          $w="100%"
          $wSM="100%"
          $selected
          $gray
          $mb="16"
        >
          Resend Code via SMS
        </Button>
        {loginHasEmail && (
          <Button
            type="button"
            onClick={() => resendCode(true)}
            $w="100%"
            $wSM="100%"
            $gray
          >
            Resend Code via Email
          </Button>
        )}
        {canFetchCode && (
          <>
            <Button
              type="button"
              onClick={handleFetchCode}
              $w="100%"
              $wSM="100%"
              $selected
              $gray
              // The SMS button above already leaves 16px; the email one does not.
              $mt={loginHasEmail ? '16' : '0'}
              $loading={fetching}
              disabled={fetching}
            >
              Get code (dev)
            </Button>
            <Disclaimer $m="8px 0 0" $color={lightTheme.lightGray}>
              Dev environment only: reads the code the server would have texted.
            </Disclaimer>
          </>
        )}
      </Form>
    </>
  );
};
